import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl, { Map as MapLibreMap, Marker } from 'maplibre-gl';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getDevices, getLastPosition, getPositions, sendCommand } from '../api/endpoints';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import type { Device, LivePosition, Position } from '../types';
import { formatDateTime, formatRelative, isOlderThan } from '../utils/dates';
import { formatLatLon } from '../utils/coordinates';

const TRAIL_LIMIT = 50;
const OFFLINE_THRESHOLD_MINUTES = 10;
const ANIM_DURATION_MS = 4800;

function animateMarker(
  marker: Marker,
  toLat: number,
  toLng: number,
  abort: { cancelled: boolean },
): void {
  const from = marker.getLngLat();
  const start = performance.now();

  const tick = (now: number) => {
    if (abort.cancelled) return;
    const t = Math.min((now - start) / ANIM_DURATION_MS, 1);
    const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    marker.setLngLat([from.lng + (toLng - from.lng) * e, from.lat + (toLat - from.lat) * e]);
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

const OSM_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: 'raster' as const,
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster' as const, source: 'osm' }],
};

function createVehicleMarkerEl(label: string, isOnline: boolean): HTMLElement {
  const W = 48;
  const ringW = 3;
  const photoSize = W - ringW * 2;
  const tailH = 9;
  const tailW = 14;
  const H = W + tailH - 1;
  const statusColor = isOnline ? '#0D9668' : '#E04545';
  const tag = (label || '???').slice(0, 3).toUpperCase();

  const wrap = document.createElement('div');
  wrap.className = 'vehicle-marker';
  wrap.style.cssText = [
    'position:relative',
    `width:${W}px`,
    `height:${H}px`,
    'cursor:pointer',
    'filter:drop-shadow(0 6px 14px rgba(15,14,12,0.30)) drop-shadow(0 2px 4px rgba(15,14,12,0.15))',
  ].join(';');

  // Colored status ring
  const ring = document.createElement('div');
  ring.className = 'vm-ring';
  ring.style.cssText = [
    'position:absolute', 'left:0', 'top:0',
    `width:${W}px`, `height:${W}px`,
    'border-radius:50%',
    `background:${statusColor}`,
    'display:flex', 'align-items:center', 'justify-content:center',
  ].join(';');

  // White inner circle with label text
  const inner = document.createElement('div');
  inner.style.cssText = [
    `width:${photoSize}px`, `height:${photoSize}px`,
    'border-radius:50%',
    'background:#FFFFFF',
    'display:flex', 'align-items:center', 'justify-content:center',
    'overflow:hidden',
  ].join(';');

  const span = document.createElement('span');
  span.textContent = tag;
  span.style.cssText = 'color:#0F0E0C;font-family:Nunito,system-ui,sans-serif;font-weight:800;font-size:11px;letter-spacing:0.04em;line-height:1;';
  inner.appendChild(span);
  ring.appendChild(inner);
  wrap.appendChild(ring);

  // Triangle tail (matches ring color)
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', String(tailW));
  svg.setAttribute('height', String(tailH));
  svg.setAttribute('viewBox', `0 0 ${tailW} ${tailH}`);
  svg.style.cssText = `position:absolute;left:${(W - tailW) / 2}px;top:${W - 1}px;`;
  const tri = document.createElementNS(svgNS, 'path');
  tri.setAttribute('d', `M 0 0 L ${tailW} 0 L ${tailW / 2} ${tailH} Z`);
  tri.setAttribute('fill', statusColor);
  svg.appendChild(tri);
  wrap.appendChild(svg);

  return wrap;
}

interface EngineConfirmDialogProps {
  device: Device;
  action: 'stop' | 'resume';
  onConfirm: () => void;
  onCancel: () => void;
  isSending: boolean;
}

function EngineConfirmDialog({
  device,
  action,
  onConfirm,
  onCancel,
  isSending,
}: EngineConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${action === 'stop' ? 'bg-red-100' : 'bg-green-100'}`}>
            <svg className={`w-5 h-5 ${action === 'stop' ? 'text-red-600' : 'text-green-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action === 'stop' ? 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' : 'M5 13l4 4L19 7'} />
            </svg>
          </div>
          <h3 className="text-gray-900 font-semibold text-lg">
            {action === 'stop' ? 'Bloquear Motor' : 'Desbloquear Motor'}
          </h3>
        </div>
        <p className="text-gray-600 text-sm mb-6">
          {action === 'stop'
            ? `Tem certeza que deseja bloquear o motor de "${device.label}"? O veículo ficará imobilizado.`
            : `Tem certeza que deseja desbloquear o motor de "${device.label}"?`}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSending}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isSending}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${action === 'stop' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-green-600 hover:bg-green-500 text-white'}`}
          >
            {isSending && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {action === 'stop' ? 'Bloquear' : 'Desbloquear'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MapPage() {
  const { user } = useAuth();
  const { subscribePosition, unsubscribePosition } = useWebSocket();
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<Map<number, Marker>>(new Map());
  const trailSourcesRef = useRef<Set<number>>(new Set());
  const trailCoordsRef = useRef<Map<number, [number, number][]>>(new Map());
  const livePositionsRef = useRef<Map<number, LivePosition>>(new Map());
  const loadingDevicesRef = useRef<Set<number>>(new Set());
  const initialZoomDoneRef = useRef(false);
  const animAbortRef = useRef<Map<number, { cancelled: boolean }>>(new Map());
  const initialCenterDoneRef = useRef(false); // auto-centered once on first live position
  const userMovedMapRef = useRef(false);       // user manually panned/zoomed → never auto-center again
  const programmaticMoveRef = useRef(false);   // suppress interaction flag during programmatic moves

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [engineDialog, setEngineDialog] = useState<{ device: Device; action: 'stop' | 'resume' } | null>(null);
  const [isSendingCommand, setIsSendingCommand] = useState(false);
  const [sidebarFilter, setSidebarFilter] = useState('');

  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: getDevices,
    refetchInterval: 60_000,
  });

  // Stable key so the positions-loading effect only reruns when device IDs change,
  // not on every lastSeenAt update from setQueryData.
  const deviceIds = useMemo(() => devices.map((d) => d.id).join(','), [devices]);

  const isOnline = useCallback(
    (device: Device) =>
      device.lastSeenAt
        ? !isOlderThan(device.lastSeenAt, OFFLINE_THRESHOLD_MINUTES)
        : false,
    [],
  );

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: OSM_STYLE,
      center: [-47.9292, -15.7801],
      zoom: 4,
      attributionControl: { compact: false },
    });

    map.once('load', () => map.resize());

    const onInteract = () => {
      if (!programmaticMoveRef.current) userMovedMapRef.current = true;
    };
    map.on('dragstart', onInteract);
    map.on('zoomstart', onInteract);

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(mapContainerRef.current);

    mapRef.current = map;

    return () => {
      observer.disconnect();
      map.off('dragstart', onInteract);
      map.off('zoomstart', onInteract);
      map.remove();
      mapRef.current = null;
      initialCenterDoneRef.current = false;
      userMovedMapRef.current = false;
    };
  }, []);

  // Load last positions and build markers + trails.
  // Uses deviceIds (stable string) as dep so this only reruns when devices are added/removed,
  // not on every lastSeenAt update via setQueryData.
  useEffect(() => {
    if (!mapRef.current || devices.length === 0) return;
    const map = mapRef.current;
    let cancelled = false;

    const loadDevicePositions = async (device: Device) => {
      if (markersRef.current.has(device.id)) return;
      if (loadingDevicesRef.current.has(device.id)) return;
      loadingDevicesRef.current.add(device.id);
      try {
        const [lastPos, posData] = await Promise.all([
          getLastPosition(device.id),
          getPositions(device.id, { limit: TRAIL_LIMIT }),
        ]);

        // Bail if this effect was superseded or marker was created by a concurrent call
        if (cancelled || markersRef.current.has(device.id)) return;

        if (!map.loaded()) await new Promise<void>((res) => map.once('load', res));
        if (cancelled || markersRef.current.has(device.id)) return;

        const online = isOnline(device);
        const markerLabel = device.label || device.imei.slice(-4);
        const markerEl = createVehicleMarkerEl(markerLabel, online);

        const popup = new maplibregl.Popup({ offset: 24, closeButton: true, maxWidth: '280px' })
          .setHTML(buildPopupHtml(device, lastPos));

        const marker = new maplibregl.Marker({ element: markerEl })
          .setLngLat([lastPos.lon, lastPos.lat])
          .setPopup(popup)
          .addTo(map);

        markersRef.current.set(device.id, marker);

        // Trail polyline
        const trailId = `trail-${device.id}`;
        if (!trailSourcesRef.current.has(device.id)) {
          const coords = posData.data.map((p) => [p.lon, p.lat] as [number, number]);
          trailCoordsRef.current.set(device.id, coords);

          if (map.getSource(trailId)) {
            map.removeLayer(trailId);
            map.removeSource(trailId);
          }

          map.addSource(trailId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: coords },
              properties: {},
            },
          });

          map.addLayer({
            id: trailId,
            type: 'line',
            source: trailId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': online ? '#10b981' : '#64748b',
              'line-width': 2.5,
              'line-opacity': 0.75,
            },
          });

          trailSourcesRef.current.add(device.id);
        }
      } catch (err) {
        console.error(`[MapPage] failed to load positions for device ${device.id}:`, err);
      } finally {
        loadingDevicesRef.current.delete(device.id);
      }
    };

    Promise.all(devices.map(loadDevicePositions)).then(() => {
      if (cancelled || initialZoomDoneRef.current) return;
      if (markersRef.current.size === 0) return;
      initialZoomDoneRef.current = true;
      if (markersRef.current.size > 1) {
        const lnglats = Array.from(markersRef.current.values()).map((m) => m.getLngLat());
        const bounds = lnglats.reduce(
          (b, ll) => b.extend(ll),
          new maplibregl.LngLatBounds(lnglats[0], lnglats[0]),
        );
        map.fitBounds(bounds, { padding: 80, maxZoom: 14, animate: false });
      }
    });

    return () => {
      cancelled = true;
      // Release loading locks so next effect can retry any in-flight loads
      devices.forEach((d) => loadingDevicesRef.current.delete(d.id));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds, isOnline]);

  // Subscribe to live position updates.
  // Deps use deviceIds (stable string) to avoid re-subscribing on every GPS update.
  // setQueryData is NOT called here — it caused a circular update cycle that tore down
  // and rebuilt subscriptions every ~8s (GPS interval). Device online status is refreshed
  // by the 60s refetchInterval on the devices query instead.
  useEffect(() => {
    const deviceList = devices;
    deviceList.forEach((device) => {
      subscribePosition(device.id, (live: LivePosition) => {
        livePositionsRef.current.set(device.id, live);

        const map = mapRef.current;
        if (!map) return;

        const marker = markersRef.current.get(device.id);
        if (marker) {
          const prev = animAbortRef.current.get(device.id);
          if (prev) prev.cancelled = true;
          const abort = { cancelled: false };
          animAbortRef.current.set(device.id, abort);

          animateMarker(marker, live.lat, live.lon, abort);

          // Auto-center once on first live position, only if user hasn't moved the map.
          // programmaticMoveRef prevents jumpTo from triggering the interaction flag.
          if (!initialCenterDoneRef.current && !userMovedMapRef.current) {
            initialCenterDoneRef.current = true;
            programmaticMoveRef.current = true;
            map.jumpTo({ center: [live.lon, live.lat], zoom: 16 });
            requestAnimationFrame(() => { programmaticMoveRef.current = false; });
          }

          const ring = marker.getElement().querySelector('.vm-ring') as HTMLElement | null;
          if (ring) ring.style.background = '#0D9668';
          const tail = marker.getElement().querySelector('svg path') as SVGPathElement | null;
          if (tail) tail.setAttribute('fill', '#0D9668');
        }

        const trailId = `trail-${device.id}`;
        const source = map.getSource(trailId) as maplibregl.GeoJSONSource | undefined;
        if (source) {
          const existing = trailCoordsRef.current.get(device.id) ?? [];
          const updated = [...existing, [live.lon, live.lat] as [number, number]].slice(-TRAIL_LIMIT);
          trailCoordsRef.current.set(device.id, updated);
          source.setData({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: updated },
            properties: {},
          });
        }
      });
    });

    return () => {
      deviceList.forEach((device) => unsubscribePosition(device.id));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds, subscribePosition, unsubscribePosition]);

  async function handleEngineCommand() {
    if (!engineDialog) return;
    setIsSendingCommand(true);
    try {
      const commandType = engineDialog.action === 'stop' ? 'engine_stop' : 'engine_resume';
      const result = await sendCommand(engineDialog.device.id, commandType, { confirm: true });
      if (result.queued) {
        toast('Comando enfileirado — será enviado quando o dispositivo reconectar.', { icon: '⏳' });
      } else {
        toast.success(`Motor ${engineDialog.action === 'stop' ? 'bloqueado' : 'desbloqueado'} com sucesso.`);
      }
      setEngineDialog(null);
    } catch {
      toast.error('Falha ao enviar comando. Tente novamente.');
    } finally {
      setIsSendingCommand(false);
    }
  }

  const onlineCount = devices.filter(isOnline).length;

  return (
    <div className="absolute inset-0 flex page-enter">
      {/* Device sidebar — fleet DeviceList style */}
      <aside className="w-[300px] flex flex-col overflow-hidden shrink-0 hidden md:flex" style={{ background: '#F7F5F0', borderRight: '1px solid #E8E6E1' }}>
        {/* Header */}
        <div style={{ padding: '20px 14px 14px', borderBottom: '1px solid #E8E6E1', background: '#FFFFFF' }}>
          <h2 style={{ fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif", fontSize: 17, fontWeight: 800, color: '#0F0E0C', letterSpacing: '-0.01em', margin: '0 0 2px', lineHeight: 1.2 }}>
            Veículos
          </h2>
          <p style={{ fontSize: 11, color: '#9B998F', margin: '0 0 12px' }}>
            <span style={{ color: '#00955F', fontWeight: 700 }}>{onlineCount}</span> online · {devices.length} total
          </p>
          <div style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#9B998F" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              value={sidebarFilter}
              onChange={(e) => setSidebarFilter(e.target.value)}
              placeholder="Nome ou IMEI..."
              style={{
                width: '100%', height: 34, paddingLeft: 28, paddingRight: 10,
                border: '1px solid #E8E6E1', borderRadius: 8, fontSize: 12.5,
                background: '#F7F5F0', color: '#0F0E0C', outline: 'none',
                boxSizing: 'border-box', fontFamily: 'inherit',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#00955F'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#E8E6E1'; }}
            />
          </div>
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {devices.length === 0 && (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#9B998F', fontSize: 12.5 }}>
              Nenhum dispositivo
            </div>
          )}
          {devices
            .filter((d) => {
              if (!sidebarFilter.trim()) return true;
              const f = sidebarFilter.trim().toLowerCase();
              return [d.label, d.imei].some((v) => v?.toLowerCase().includes(f));
            })
            .map((device) => {
              const online = isOnline(device);
              const isSelected = selectedDevice?.id === device.id;
              const statusColor = online ? '#0D9668' : '#9B998F';
              const statusBg = online ? '#E6F5EE' : '#EFEDE8';
              const label = device.label || device.imei.slice(-8);
              return (
                <button
                  key={device.id}
                  onClick={() => {
                    setSelectedDevice(device);
                    const marker = markersRef.current.get(device.id);
                    if (marker) {
                      marker.togglePopup();
                      programmaticMoveRef.current = true;
                      mapRef.current?.flyTo({ center: marker.getLngLat(), zoom: 16 });
                      setTimeout(() => { programmaticMoveRef.current = false; }, 1500);
                    }
                  }}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: 14,
                    background: '#FFFFFF', border: 'none', cursor: 'pointer',
                    boxShadow: isSelected
                      ? '0 0 0 1.5px #00955F55, 0 2px 8px rgba(15,14,12,0.08)'
                      : '0 1px 2px rgba(15,14,12,0.06), 0 0 0 1px rgba(15,14,12,0.06)',
                    transition: 'box-shadow 0.15s',
                  }}
                >
                  <span style={{
                    flexShrink: 0, width: 44, height: 44, borderRadius: 12,
                    background: statusBg, border: '1px solid #E8E6E1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width={20} height={20} viewBox="0 0 24 24" fill={statusColor}>
                      <path d="M20 8h-3V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v9h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-2zM6 15.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9 1.96 2.5H17V6.5h2.5zM18 15.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{
                      fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif",
                      fontWeight: 800, fontSize: 14, color: '#0F0E0C',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1,
                    }}>
                      {label}
                    </span>
                    <span style={{ fontSize: 11, color: '#9B998F', lineHeight: 1 }}>
                      {device.lastSeenAt ? formatRelative(device.lastSeenAt) : 'Nunca visto'}
                    </span>
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: statusColor }}>
                    {online ? 'Online' : 'Offline'}
                  </span>
                </button>
              );
          })}
        </div>
      </aside>

      {/* Map container */}
      <div className="relative flex-1">
        <div ref={mapContainerRef} style={{ position: 'absolute', inset: 0 }} />

        {/* Admin floating commands */}
        {user?.role === 'admin' && selectedDevice && (
          <div className="absolute bottom-6 right-5 flex flex-col gap-2 z-10">
            <button
              onClick={() => setEngineDialog({ device: selectedDevice, action: 'stop' })}
              className="flex items-center gap-2 px-3.5 py-2 text-[13px] font-semibold text-white rounded-lg shadow-2xl transition-all"
              style={{ background: 'rgba(239,68,68,0.9)', backdropFilter: 'blur(8px)', border: '1px solid rgba(239,68,68,0.5)' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Bloquear Motor
            </button>
            <button
              onClick={() => setEngineDialog({ device: selectedDevice, action: 'resume' })}
              className="flex items-center gap-2 px-3.5 py-2 text-[13px] font-semibold text-white rounded-lg shadow-2xl transition-all"
              style={{ background: 'rgba(16,185,129,0.9)', backdropFilter: 'blur(8px)', border: '1px solid rgba(16,185,129,0.5)' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Desbloquear Motor
            </button>
          </div>
        )}

        {/* History shortcut + Re-center button */}
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
          {selectedDevice && (
            <button
              onClick={() => navigate(`/history?deviceId=${selectedDevice.id}`)}
              className="flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-slate-700 rounded-lg shadow-xl transition-all hover:shadow-2xl"
              style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', border: '1px solid rgba(0,0,0,0.08)' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ver Histórico
            </button>
          )}
          <button
            onClick={() => {
              const map = mapRef.current;
              if (!map) return;
              // Find first live position to re-center on
              const [firstDeviceId, livePos] = [...livePositionsRef.current.entries()][0] ?? [];
              if (livePos) {
                programmaticMoveRef.current = true;
                map.flyTo({ center: [livePos.lon, livePos.lat], zoom: 16 });
                setTimeout(() => { programmaticMoveRef.current = false; }, 1500);
              } else if (firstDeviceId !== undefined) {
                const marker = markersRef.current.get(firstDeviceId);
                if (marker) {
                  programmaticMoveRef.current = true;
                  map.flyTo({ center: marker.getLngLat(), zoom: 16 });
                  setTimeout(() => { programmaticMoveRef.current = false; }, 1500);
                }
              }
            }}
            title="Centralizar no veículo"
            className="flex items-center justify-center w-9 h-9 rounded-lg shadow-xl transition-all hover:shadow-2xl self-end"
            style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', border: '1px solid rgba(0,0,0,0.08)' }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Engine command confirm dialog */}
      {engineDialog && (
        <EngineConfirmDialog
          device={engineDialog.device}
          action={engineDialog.action}
          onConfirm={handleEngineCommand}
          onCancel={() => setEngineDialog(null)}
          isSending={isSendingCommand}
        />
      )}
    </div>
  );
}

function buildPopupHtml(device: Device, pos: Position): string {
  const speed = pos.speedKmh != null ? `${pos.speedKmh.toFixed(1)} km/h` : '—';
  const osmUrl = `https://www.openstreetmap.org/?mlat=${pos.lat}&mlon=${pos.lon}&zoom=16`;
  return `
    <div style="font-family:Nunito,system-ui,sans-serif;min-width:200px;background:#FFFFFF;color:#0F0E0C;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <div style="width:8px;height:8px;border-radius:50%;background:#0D9668;flex-shrink:0;"></div>
        <p style="font-size:14px;font-weight:800;margin:0;color:#0F0E0C;letter-spacing:-0.01em;">${device.label || device.imei}</p>
      </div>
      <div style="display:grid;gap:6px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span style="font-size:11px;color:#9B998F;font-weight:600;">Velocidade</span>
          <span style="font-size:12px;color:#0F0E0C;font-weight:700;background:#F7F5F0;padding:2px 8px;border-radius:6px;">${speed}</span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span style="font-size:11px;color:#9B998F;font-weight:600;">Horário</span>
          <span style="font-size:11px;color:#6B6860;">${formatDateTime(pos.deviceTime)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span style="font-size:11px;color:#9B998F;font-weight:600;">Coordenadas</span>
          <span style="font-size:11px;color:#6B6860;">${formatLatLon(pos.lat, pos.lon)}</span>
        </div>
      </div>
      <a href="${osmUrl}" target="_blank" rel="noopener"
        style="display:flex;align-items:center;gap:5px;font-size:11px;color:#00955F;text-decoration:none;font-weight:700;padding:6px 0;border-top:1px solid #E8E6E1;">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
        </svg>
        Ver no OpenStreetMap
      </a>
    </div>
  `;
}
